import type React from "react";
import { Box, Typography, Button, useTheme } from "@mui/material";
import { Print } from "@mui/icons-material";
import { motion } from "framer-motion";
import QrCode from "react-qr-code";
import pattern from "../../../assets/pattern.png";
import Muzukuruimg1 from "../../../assets/Muzukuruimg1.png";
import Muzukuruimg3 from "../../../assets/Muzukuruimg3.png";
import muzukurublack from "../../../assets/muzukurublack.png";

const imageBoxStyle = {
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-end",
  height: "60px",
  width: "80%",
};

const TableTentPrintPage: React.FC = () => {
  const theme = useTheme();
  const website_url = "https://www.muzukuru.com";
  const handlePrint = (): void => {
    window.print();
  };

  return (
    <Box
      sx={{
        width: "75vw",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        "@media print": {
          width: "100vw",
          height: "100vh",
          margin: 0,
          padding: 0,
          overflow: "hidden",
          WebkitPrintColorAdjust: "exact",
          colorAdjust: "exact",
        },
      }}
    >
      <Box
        className="no-print"
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",

          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Button startIcon={<Print />} onClick={handlePrint} color="primary">
          Print Table Tent
        </Button>
      </Box>

      <Box
        id="printable-area"
        sx={{
          flexGrow: 1,
          background: `url(${pattern}), linear-gradient(135deg, #f5f1eb 0%, #ede4d3 100%)`,
          backgroundSize: "cover, 100% 100%",
          backgroundPosition: "center, center",
          backgroundRepeat: "no-repeat, no-repeat",
          padding: 2,
          position: "relative",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          "@media print": {
            padding: "0 !important",
            margin: "0 !important",
            height: "100vh !important",
            width: "100vw !important",
            WebkitPrintColorAdjust: "exact",
            colorAdjust: "exact",
            pageBreakAfter: "avoid",
            pageBreakInside: "avoid",
          },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              radial-gradient(circle at 20% 20%, rgba(139, 69, 19, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 80% 80%, rgba(139, 69, 19, 0.1) 0%, transparent 50%),
              linear-gradient(45deg, rgba(139, 69, 19, 0.05) 25%, transparent 25%),
              linear-gradient(-45deg, rgba(139, 69, 19, 0.05) 25%, transparent 25%)
            `,
            backgroundSize: "100px 100px, 100px 100px, 20px 20px, 20px 20px",
            zIndex: 0,
            "@media print": {
              WebkitPrintColorAdjust: "exact",
              colorAdjust: "exact",
            },
          },
        }}
      >
        <Box
          component={motion.div}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className="tent-container"
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "calc(100% - 4px)",
            height: "calc(100% - 4px)",
            position: "relative",
            zIndex: 1,
            border: "2px solid #000",
            boxSizing: "border-box",
            "@media print": {
              transform: "scale(0.9)",
              transformOrigin: "center center",
              width: "100% !important",
              height: "100% !important",
              margin: "0 !important",
              padding: "0 !important",
              pageBreakAfter: "avoid !important",
              pageBreakInside: "avoid !important",
              overflow: "hidden !important",
            },
          }}
        >
          {/* First Row */}
          <Box
            sx={{
              display: "flex",
              width: "100%",
              height: "50%",
              "@media print": {
                height: "50% !important",
              },
            }}
          >
            {/* Panel 1 - Free WiFi */}
            <Box
              className="tent-section"
              sx={{
                width: "25%",
                padding: 2,
                borderRight: "2px solid #000",
                boxSizing: "border-box",
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                alignItems: "center",
                "@media print": {
                  pageBreakInside: "avoid !important",
                },
              }}
            >
              <Box
                sx={{
                  flexGrow: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  pt: 2,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ color: "#483C32", fontWeight: "medium", mb: 2 }}
                >
                  FREE WIFI
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight="ExtraBold"
                  sx={{
                    color: "#483C32",
                    fontSize: "14px",
                    fontFamily: "Montserrat, sans-serif",
                    flexGrow: 1,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  MYMUZUKURU
                </Typography>
              </Box>
              <Box sx={imageBoxStyle}>
                <img
                  alt="Muzukuruimg1"
                  width="80px"
                  height="60px"
                  src={Muzukuruimg1 || "/placeholder.svg"}
                  style={{
                    objectFit: "contain",
                    WebkitPrintColorAdjust: "exact",
                    colorAdjust: "exact",
                    printColorAdjust: "exact",
                  }}
                />
              </Box>
            </Box>

            {/* Panel 2 - QR Code */}
            <Box
              className="tent-section"
              sx={{
                width: "25%",
                padding: 2,
                borderRight: "2px solid #000",
                boxSizing: "border-box",
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                alignItems: "center",
                "@media print": {
                  pageBreakInside: "avoid !important",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  mb: 2,
                  mt: 2,
                }}
              >
                <QrCode size={80} value={website_url} />
              </Box>
              <Typography
                variant="body1"
                sx={{
                  color: "#483C32",
                  fontWeight: "bold",
                  fontSize: "14px",
                  fontFamily: "Montserrat, sans-serif",
                  mb: 2,
                  textAlign: "center",
                }}
              >
                SCAN CODE TO POST YOUR MESSAGE OR COMMENT
              </Typography>
              <Box sx={imageBoxStyle}>
                <img
                  alt="Muzukurulogo"
                  width="160px"
                  height="80px"
                  src={muzukurublack || "/placeholder.svg"}
                  style={{
                    objectFit: "contain",
                    WebkitPrintColorAdjust: "exact",
                    colorAdjust: "exact",
                    printColorAdjust: "exact",
                  }}
                />
              </Box>
            </Box>

            {/* Panel 3 - Memory Table */}
            <Box
              className="tent-section"
              sx={{
                width: "25%",
                padding: 2,
                borderRight: "2px solid #000",
                boxSizing: "border-box",
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                alignItems: "center",
                "@media print": {
                  pageBreakInside: "avoid !important",
                },
              }}
            >
              <Box
                sx={{
                  flexGrow: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  pt: 2,
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    color: "#483C32",
                    fontWeight: "medium",
                    fontSize: "14px",
                    fontFamily: "Montserrat, sans-serif",
                    mb: 1,
                  }}
                >
                  VISIT THE
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight="ExtraBold"
                  sx={{
                    color: "#483C32",
                    fontSize: "14px",
                    fontFamily: "Montserrat, sans-serif",
                    mb: 2,
                    flexGrow: 1,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  MEMORY TABLE
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "#483C32",
                    fontWeight: "bold",
                    fontSize: "14px",
                    fontFamily: "Montserrat, sans-serif",
                  }}
                >
                  TO LEAVE A<br />
                  HANDWRITTEN
                  <br />
                  MESSAGE
                </Typography>
              </Box>
              <Box sx={imageBoxStyle}>
                <img
                  alt="Muzukuruimg3"
                  width="80px"
                  height="60px"
                  src={Muzukuruimg3 || "/placeholder.svg"}
                  style={{
                    objectFit: "contain",
                    WebkitPrintColorAdjust: "exact",
                    colorAdjust: "exact",
                    printColorAdjust: "exact",
                  }}
                />
              </Box>
            </Box>

            {/* Panel 4 - Decorative */}
            <Box
              className="tent-section"
              sx={{
                width: "25%",
                boxSizing: "border-box",
                "@media print": {
                  pageBreakInside: "avoid !important",
                },
              }}
            />
          </Box>

          {/* Second Row - Mirror of first row */}
          <Box
            sx={{
              display: "flex",
              width: "100%",
              height: "50%",
              borderTop: "2px solid #000",
              "@media print": {
                height: "50% !important",
              },
            }}
          >
            <Box
              className="tent-section"
              sx={{
                width: "25%",
                padding: 2,
                borderRight: "2px solid #000",
                boxSizing: "border-box",
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                alignItems: "center",
                "@media print": {
                  pageBreakInside: "avoid !important",
                },
              }}
            >
              <Box
                sx={{
                  flexGrow: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  pt: 2,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ color: "#483C32", fontWeight: "medium", mb: 2 }}
                >
                  FREE WIFI
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight="ExtraBold"
                  sx={{
                    color: "#483C32",
                    fontSize: "14px",
                    fontFamily: "Montserrat, sans-serif",
                    flexGrow: 1,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  MYMUZUKURU
                </Typography>
              </Box>
              <Box sx={imageBoxStyle}>
                <img
                  alt="Muzukuruimg1"
                  width="80px"
                  height="60px"
                  src={Muzukuruimg1 || "/placeholder.svg"}
                  style={{
                    objectFit: "contain",
                    WebkitPrintColorAdjust: "exact",
                    colorAdjust: "exact",
                    printColorAdjust: "exact",
                  }}
                />
              </Box>
            </Box>

            {/* Bottom Panel 2 - QR Code */}
            <Box
              className="tent-section"
              sx={{
                width: "25%",
                padding: 2,
                borderRight: "2px solid #000",
                boxSizing: "border-box",
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                alignItems: "center",
                "@media print": {
                  pageBreakInside: "avoid !important",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  mb: 2,
                  mt: 2,
                }}
              >
                <QrCode size={80} value={website_url} />
              </Box>
              <Typography
                variant="body1"
                sx={{
                  color: "#483C32",
                  fontWeight: "bold",
                  fontSize: "14px",
                  fontFamily: "Montserrat, sans-serif",
                  mb: 2,
                  textAlign: "center",
                }}
              >
                SCAN CODE TO POST YOUR MESSAGE OR COMMENT
              </Typography>
              <Box sx={imageBoxStyle}>
                <img
                  alt="Muzukurulogo"
                  width="160px"
                  height="80px"
                  src={muzukurublack || "/placeholder.svg"}
                  style={{
                    objectFit: "contain",
                    WebkitPrintColorAdjust: "exact",
                    colorAdjust: "exact",
                    printColorAdjust: "exact",
                  }}
                />
              </Box>
            </Box>

            {/* Bottom Panel 3 - Memory Table */}
            <Box
              className="tent-section"
              sx={{
                width: "25%",
                padding: 2,
                borderRight: "2px solid #000",
                boxSizing: "border-box",
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                alignItems: "center",
                "@media print": {
                  pageBreakInside: "avoid !important",
                },
              }}
            >
              <Box
                sx={{
                  flexGrow: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  pt: 2,
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    color: "#483C32",
                    fontWeight: "medium",
                    fontSize: "14px",
                    fontFamily: "Montserrat, sans-serif",
                    mb: 1,
                  }}
                >
                  VISIT THE
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight="ExtraBold"
                  sx={{
                    color: "#483C32",
                    fontSize: "14px",
                    fontFamily: "Montserrat, sans-serif",
                    mb: 2,
                    flexGrow: 1,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  MEMORY TABLE
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "#483C32",
                    fontWeight: "bold",
                    fontSize: "14px",
                    fontFamily: "Montserrat, sans-serif",
                  }}
                >
                  TO LEAVE A<br />
                  HANDWRITTEN
                  <br />
                  MESSAGE
                </Typography>
              </Box>
              <Box sx={imageBoxStyle}>
                <img
                  alt="Muzukuruimg3"
                  width="80px"
                  height="60px"
                  src={Muzukuruimg3 || "/placeholder.svg"}
                  style={{
                    objectFit: "contain",
                    WebkitPrintColorAdjust: "exact",
                    colorAdjust: "exact",
                    printColorAdjust: "exact",
                  }}
                />
              </Box>
            </Box>

            <Box
              className="tent-section"
              sx={{
                width: "2%",
                boxSizing: "border-box",
                "@media print": {
                  pageBreakInside: "avoid !important",
                },
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default TableTentPrintPage;
